'use client';

import React from 'react';

import { usePlacesAutocomplete } from './usePlacesAutocomplete';
import {
  buildAddressValueFromPlaceDetails,
  matchStateKey,
  normalizeAddressValue,
  useStateOptions,
} from './addressUtils';
import type { AddressValue, PlaceDetails, StateOption } from './types';

export type AddressAutocompleteFieldProps = {
  value: AddressValue;
  onChange: (next: AddressValue) => void;
  stateOptions?: StateOption[];
  stateLock?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  readOnly?: boolean;
  className?: string;
  inputClassName?: string;
  optionalIndicator?: React.ReactNode;
  conflictMessage?: string;
};

const findStateOption = (input: string | undefined, options: StateOption[]): StateOption | undefined => {
  const key = matchStateKey(input, options);
  if (!key) return undefined;
  return options.find((option) => option.key === key);
};

const defaultInputClassName =
  'w-full px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-2 focus:ring-blue-500';

export default function AddressAutocompleteField({
  value,
  onChange,
  stateOptions: stateOptionsProp,
  stateLock,
  label,
  placeholder,
  required = false,
  readOnly = false,
  className,
  inputClassName,
  optionalIndicator,
  conflictMessage,
}: AddressAutocompleteFieldProps) {
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  const listId = React.useId();

  const { stateOptions: rawStateOptions } = useStateOptions();
  const stateOptions = React.useMemo(() => {
    if (stateOptionsProp && stateOptionsProp.length > 0) {
      return stateOptionsProp;
    }

    return rawStateOptions
      .filter((s) => s.key && s.display)
      .map((s) => ({
        key: s.key,
        display: s.display,
        code: s.code || s.key,
      }));
  }, [rawStateOptions, stateOptionsProp]);

  const latestContextRef = React.useRef({
    value,
    stateOptions,
    stateLock,
  });
  latestContextRef.current = {
    value,
    stateOptions,
    stateLock,
  };

  const onSelect = React.useCallback((placeDetails: PlaceDetails) => {
    const { value: currentValue, stateOptions: currentStateOptions, stateLock: currentStateLock } = latestContextRef.current;

    const nextValue = buildAddressValueFromPlaceDetails(
      placeDetails,
      normalizeAddressValue(currentValue),
      currentStateOptions,
      currentStateLock
    );

    onChange(nextValue);
  }, [onChange]);

  const {
    isGoogleMapsLoaded,
    suggestions,
    showSuggestions,
    isLoading,
    inputRef,
    suggestionsRef,
    requestSuggestions,
    selectSuggestion,
    openIfHasSuggestions,
    hideSuggestions,
  } = usePlacesAutocomplete({ onSelect, debounceDelay: 300 });

  React.useEffect(() => {
    setFocusedIndex(-1);
  }, [suggestions]);

  const stateHint = React.useMemo(() => {
    const selectedState = findStateOption(stateLock || value.state, stateOptions);
    if (!selectedState) return undefined;
    return {
      display: selectedState.display,
      code: selectedState.code || selectedState.key,
    };
  }, [stateLock, stateOptions, value.state]);

  const handleInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) {
      return;
    }

    const nextLine1 = event.target.value;
    const nextValue: AddressValue = {
      ...normalizeAddressValue(value, stateLock),
      line1: nextLine1,
    };

    onChange(nextValue);
    setFocusedIndex(-1);

    requestSuggestions(nextLine1, stateHint);
  }, [onChange, readOnly, requestSuggestions, stateHint, stateLock, value]);

  const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setFocusedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        event.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
          void selectSuggestion(suggestions[focusedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        hideSuggestions();
        setFocusedIndex(-1);
        break;
      default:
        break;
    }
  }, [focusedIndex, hideSuggestions, selectSuggestion, showSuggestions, suggestions]);

  const mergedInputClassName = `${inputClassName || defaultInputClassName} ${
    conflictMessage ? 'loanapp-conflict-field-border loanapp-conflict-field-focus' : ''
  }`.trim();

  return (
    <div className={className || ''}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value.line1 || ''}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className={mergedInputClassName}
          placeholder={placeholder}
          required={required}
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          autoComplete="off"
          role="combobox"
          aria-expanded={showSuggestions}
          aria-controls={listId}
          onFocus={() => {
            if (!readOnly) {
              openIfHasSuggestions();
            }
          }}
          data-testid="address-autocomplete-input"
        />
        {optionalIndicator}

        {showSuggestions && (
          <div
            id={listId}
            role="listbox"
            ref={suggestionsRef}
            className="absolute top-full left-0 right-0 bg-white border border-blue-500 loanapp-radius shadow-lg z-50 max-h-60 overflow-y-auto mt-1"
            data-testid="address-suggestions-listbox"
            onMouseDown={(e) => e.preventDefault()}
          >
            {isLoading ? (
              <div className="p-3 text-gray-500 text-center">Loading suggestions...</div>
            ) : suggestions.length > 0 ? (
              suggestions.map((suggestion, index) => (
                <div
                  key={suggestion.placeId || index}
                  role="option"
                  aria-selected={index === focusedIndex}
                  className={`p-3 cursor-pointer border-b border-blue-100 last:border-b-0 ${
                    index === focusedIndex ? 'bg-blue-50' : 'hover:bg-blue-50'
                  }`}
                  onClick={() => void selectSuggestion(suggestion)}
                  onMouseEnter={() => setFocusedIndex(index)}
                  data-testid={`address-suggestion-option-${index}`}
                >
                  <div className="font-medium text-gray-900">{suggestion.mainText || suggestion.description}</div>
                  {suggestion.secondaryText && (
                    <div className="text-sm text-gray-500">{suggestion.secondaryText}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="p-3 text-gray-500 text-center">No suggestions found</div>
            )}
          </div>
        )}

        {!isGoogleMapsLoaded && (
          <p className="mt-1 text-sm text-gray-500">Loading address suggestions...</p>
        )}

        {conflictMessage && (
          <div className="mt-1 text-sm text-amber-600">
            {conflictMessage}
          </div>
        )}
      </div>
    </div>
  );
}
